import React from 'react';
import { 
  X, User, Star, MapPin, Phone, Globe, Mail, Calendar, Euro, AlertTriangle, ExternalLink,
  Instagram, Facebook, Youtube, Linkedin, Twitter, MessageCircle, Check
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { getLocalizedText, formatPrice } from '../../utils/helpers';

// Detail Panel for Preview
export const DetailPanel = ({ item, type, onApprove, onReject, onClose }) => {
  if (!item) return null;
  
  const isEdit = type?.includes('-edit');
  const baseType = type?.replace('-edit', '');
  
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center overflow-y-auto py-8">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl mx-4 my-8 animate-fade-in">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-6 py-4 rounded-t-xl flex justify-between items-center z-10">
          <div>
            <h2 className="text-xl font-bold text-gray-800">
              {isEdit ? 'Revisión de Modificación' : 'Nueva Solicitud'}
            </h2>
            <p className="text-sm text-gray-500">
              {baseType === 'service' && 'Servicio'}
              {baseType === 'course' && 'Curso'}
              {baseType === 'teacher' && 'Profesional'}
              {baseType === 'school' && 'Escuela'}
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X className="h-5 w-5" />
          </button>
        </div>
        
        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Main Info */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Left: Images and basic info */}
            <div className="space-y-4">
              {(item.images?.[0] || item.avatar) && (
                <img 
                  src={item.images?.[0] || item.avatar} 
                  alt="" 
                  className="w-full h-48 object-cover rounded-lg"
                />
              )}
              
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <h3 className="font-semibold text-lg">
                  {getLocalizedText(item.title || item.name, 'es') || item.user_name || 'Sin nombre'}
                </h3>
                
                {item.user_name && (
                  <div className="flex items-center gap-2 text-sm">
                    <User className="h-4 w-4 text-gray-400" />
                    <span>{item.user_name}</span>
                    {item.user_email && <span className="text-gray-400">({item.user_email})</span>}
                  </div>
                )}
                
                {item.location && (
                  <div className="flex items-center gap-2 text-sm">
                    <MapPin className="h-4 w-4 text-gray-400" />
                    <span>{item.location.city}, {item.location.province || item.location.country}</span>
                  </div>
                )}
                
                {(item.price !== undefined || item.price !== null) && (
                  <div className="flex items-center gap-2 text-sm">
                    <Euro className="h-4 w-4 text-gray-400" />
                    <span className="font-medium">{formatPrice(item.price, item.currency || 'EUR')}</span>
                    {item.duration_minutes && <span className="text-gray-400">/ {item.duration_minutes} min</span>}
                  </div>
                )}
                
                {item.start_date && (
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    <span>{new Date(item.start_date).toLocaleDateString('es-ES')}</span>
                    {item.end_date && <span>- {new Date(item.end_date).toLocaleDateString('es-ES')}</span>}
                  </div>
                )}
              </div>
            </div>
            
            {/* Right: Details */}
            <div className="space-y-4">
              {/* Description */}
              {(item.description || item.bio) && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium mb-2">Descripción</h4>
                  <p className="text-sm text-gray-600 whitespace-pre-wrap">
                    {getLocalizedText(item.description || item.bio, 'es')}
                  </p>
                </div>
              )}
              
              {/* Contact Info */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <h4 className="font-medium mb-2">Contacto</h4>
                {item.phone && (
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="h-4 w-4 text-gray-400" />
                    <span>{item.phone}</span>
                  </div>
                )}
                {item.website && (
                  <div className="flex items-center gap-2 text-sm">
                    <Globe className="h-4 w-4 text-gray-400" />
                    <a href={item.website} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                      {item.website}
                    </a>
                  </div>
                )}
                {item.user_email && (
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="h-4 w-4 text-gray-400" />
                    <span>{item.user_email}</span>
                  </div>
                )}
              </div>
              
              {/* Social Links */}
              {item.social_links && Object.keys(item.social_links).length > 0 && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium mb-2">Redes Sociales</h4>
                  <div className="flex gap-3 flex-wrap">
                    {Object.entries(item.social_links).map(([key, url]) => {
                      if (!url) return null;
                      const icons = {
                        instagram: Instagram,
                        facebook: Facebook,
                        youtube: Youtube,
                        linkedin: Linkedin,
                        twitter: Twitter,
                        tiktok: MessageCircle,
                        telegram: MessageCircle
                      };
                      const Icon = icons[key] || Globe;
                      return (
                        <a key={key} href={url} target="_blank" rel="noopener noreferrer" 
                           className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-full border hover:bg-gray-50 text-sm">
                          <Icon className="h-4 w-4" />
                          <span className="capitalize">{key}</span>
                        </a>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* Google Business Rating */}
              {item.google_business_rating && (
                <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <Star className="h-4 w-4 text-yellow-500" />
                    Google Business
                  </h4>
                  <div className="space-y-1 text-sm">
                    <p><strong>Rating:</strong> {item.google_business_rating.google_rating} / 5</p>
                    <p><strong>Reviews:</strong> {item.google_business_rating.google_reviews_count}</p>
                    {item.google_business_rating.google_maps_url && (
                      <a href={item.google_business_rating.google_maps_url} target="_blank" rel="noopener noreferrer" 
                         className="text-primary hover:underline flex items-center gap-1">
                        Ver en Google Maps <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                    <Badge className={item.google_business_rating.is_verified ? 'bg-green-500' : 'bg-orange-500'}>
                      {item.google_business_rating.is_verified ? 'Verificado' : 'Pendiente verificación'}
                    </Badge>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Edit Comparison (if editing) */}
          {isEdit && item.edit_pending && (
            <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-orange-500" />
                Cambios Pendientes
              </h4>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                {Object.entries(item.edit_pending).map(([key, value]) => (
                  <div key={key} className="bg-white rounded p-3">
                    <p className="font-medium text-gray-500 capitalize mb-1">{key.replace(/_/g, ' ')}</p>
                    <p className="text-gray-800">
                      {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Additional Info */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            {item.service_type && (
              <div className="bg-gray-50 rounded p-3">
                <p className="text-gray-500">Tipo</p>
                <p className="font-medium">{item.service_type}</p>
              </div>
            )}
            {item.course_type && (
              <div className="bg-gray-50 rounded p-3">
                <p className="text-gray-500">Tipo</p>
                <p className="font-medium">{item.course_type}</p>
              </div>
            )}
            {item.target_audience && (
              <div className="bg-gray-50 rounded p-3">
                <p className="text-gray-500">Público</p>
                <p className="font-medium">{Array.isArray(item.target_audience) ? item.target_audience.join(', ') : item.target_audience}</p>
              </div>
            )}
            {item.specialties && (
              <div className="bg-gray-50 rounded p-3">
                <p className="text-gray-500">Especialidades</p>
                <p className="font-medium">{item.specialties.join(', ')}</p>
              </div>
            )}
            {item.is_online !== undefined && (
              <div className="bg-gray-50 rounded p-3">
                <p className="text-gray-500">Modalidad</p>
                <p className="font-medium">{item.is_online ? 'Online' : 'Presencial'}</p>
              </div>
            )}
            {item.created_at && (
              <div className="bg-gray-50 rounded p-3">
                <p className="text-gray-500">Creado</p>
                <p className="font-medium">{new Date(item.created_at).toLocaleDateString('es-ES')}</p>
              </div>
            )}
          </div>
          
          {/* All Images Gallery */}
          {item.images && item.images.length > 1 && (
            <div>
              <h4 className="font-medium mb-3">Galería ({item.images.length} imágenes)</h4>
              <div className="grid grid-cols-4 gap-2">
                {item.images.map((img, idx) => (
                  <img key={idx} src={img} alt="" className="w-full h-24 object-cover rounded-lg" />
                ))}
              </div>
            </div>
          )}
        </div>
        
        {/* Footer Actions */}
        <div className="sticky bottom-0 bg-white border-t px-6 py-4 rounded-b-xl flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="destructive" onClick={onReject} className="gap-2">
            <X className="h-4 w-4" /> Rechazar
          </Button>
          <Button onClick={onApprove} className="bg-green-500 hover:bg-green-600 gap-2">
            <Check className="h-4 w-4" /> Aprobar
          </Button>
        </div>
      </div>
    </div>
  );
};
